import {
  handleFetchResponse,
  getFormURLEncodedBody,
  FetchApiError,
} from '@stripe-internal/connect-embedded-lib';
import type {StripeAccountFragmentData} from '@stripe-internal/connect-embedded-lib/dist/ExternalAccounts/queries/stripeAccountFragment';
import {mountInvisible} from '@stripe-internal/stripethirdparty';
import {getDevLogger} from '../utils/getLogger';
import type {RiskSignalsCollectionDataLayerResponse} from './types';
import type {
  IFrameCollectRiskSignalsRequest,
  IFrameCollectRiskSignalsResponse,
  IFrameMessageFetchErrorResponse,
  IFrameMessageFailedResponse,
} from '../data-layer-client/FrameMessenger';
import {authStore} from './authentication/AuthenticationStore';
import {getCurrentScriptUrlContext} from '../utils/getCurrentScriptUrlContext';
import {getStripeApiHost} from '../data-layer-client/buildStripeClient';
import {getDataLayerHeaders} from './proxyRequest';
import {getClaimInfo} from './DataLayer';
import {serializeFetchErrorForPostMessage} from '../data-layer-client/utils';
import {promiseWithTimeout} from '../utils/promiseWithTimeout';

const devLogger = getDevLogger();

const RISK_SIGNALS_REQUIREMENT_NAME = 'risk_signals.session_metadata';
const RISK_SIGNAL_TIMEOUT = 10000; // 10 seconds

interface HCaptchaData {
  response: string;
  key?: string;
}

interface CollectedRiskSignals {
  iovationBlackbox: string | null;
  hcaptchaData: HCaptchaData | null;
}

/**
 * Collect and send risk signals to Stripe via the /v1/risk_signals endpoint
 * @param request {IFrameCollectRiskSignalsRequest} The data layer request
 * @returns {IFrameCollectRiskSignalsResponse | IFrameMessageFetchErrorResponse | IFrameMessageFailedResponse} The data layer response. Return standard data layer error response types if an error occurs.
 * - IFrameCollectRiskSignalsResponse: If the risk signals are collected and sent successfully
 * - IFrameMessageFetchErrorResponse: If an API error occurs
 * - IFrameMessageFailedResponse: If an error occurs that is not an API error
 */
export async function collectAndSendRiskSignals(
  request: IFrameCollectRiskSignalsRequest,
): Promise<
  | IFrameCollectRiskSignalsResponse
  | IFrameMessageFetchErrorResponse
  | IFrameMessageFailedResponse
> {
  devLogger.info('Data Layer: collectAndSendRiskSignals called');

  try {
    let response: RiskSignalsCollectionDataLayerResponse;

    // Check if the account has risk signals requirement
    const hasRiskSignalsRequirement =
      await doesAccountHaveRiskSignalsRequirement();
    if (!hasRiskSignalsRequirement) {
      devLogger.info('Account does not have risk signals requirement');
      response = {
        status: 'skipped',
        skippedReason: 'requirement_fulfilled',
      };
    } else {
      devLogger.info(
        'Account has risk signals requirement, collecting and sending risk signals to Stripe',
      );
      // Collect and send risk signals to Stripe
      const collectedSignals = await collectRiskSignals();
      await sendRiskSignalsToStripe(collectedSignals);

      response = {
        status: 'completed',
      };
    }

    return {
      id: request.id,
      type: request.type,
      requestType: 'collectRiskSignalsResponse',
      response,
    };
  } catch (error) {
    devLogger.error('Error in collectAndSendRiskSignals:', error);
    return createErrorResponse(request, error);
  }
}

function createErrorResponse(
  request: IFrameCollectRiskSignalsRequest,
  error: unknown,
): IFrameMessageFailedResponse | IFrameMessageFetchErrorResponse {
  // Use standard error response types like other data layer functions
  if (error instanceof FetchApiError) {
    return {
      id: request.id,
      type: request.type,
      requestType: 'fetchErrorResponse',
      error: serializeFetchErrorForPostMessage(error),
    };
  } else {
    return {
      id: request.id,
      type: request.type,
      requestType: 'errorResponse',
      error: error instanceof Error ? error : new Error(String(error)),
    };
  }
}

/**
 * Check if the account has risk signals requirement
 * @returns True if the account risk_signals.session_metadata requirement exists, false otherwise
 */
async function doesAccountHaveRiskSignalsRequirement(): Promise<boolean> {
  const accountId = getClaimInfo()?.connectedAccountId;
  if (!accountId) {
    // this should never happen as risk signal collection is only called after authentication success which sets the claim info
    throw new Error('No valid connectedAccountId found in claimInfo');
  }

  const apiUrl = buildApiUrl(`/v1/accounts/${accountId}`);
  const fetchResponse = await fetch(apiUrl, {
    method: 'GET',
    headers: buildApiRequestHeaders(),
  });

  const accountData: StripeAccountFragmentData = await handleFetchResponse(
    fetchResponse,
  );

  // Check if risk_signals.session_metadata requirement exists
  return (
    accountData.requirements?.currently_due?.includes(
      RISK_SIGNALS_REQUIREMENT_NAME,
    ) || false
  );
}

/**
 * Helper to collect a risk signal with timeout and graceful error handling.
 * Returns null if collection fails or times out.
 */
async function collectSignalWithTimeout<T>(
  signalName: string,
  collectionFn: () => Promise<T>,
  timeoutMs: number = RISK_SIGNAL_TIMEOUT,
): Promise<T | null> {
  try {
    return await promiseWithTimeout(
      collectionFn(),
      timeoutMs,
      `${signalName} collection timed out`,
    );
  } catch (error) {
    devLogger.error(`${signalName} collection failed with error: ${error}`);
    return null;
  }
}

/**
 * Collect all risk signals (Iovation, hCaptcha, etc.)
 * Each signal collection is wrapped with a timeout to ensure graceful degradation
 * @returns {CollectedRiskSignals} Collected risk signals data
 */
async function collectRiskSignals(): Promise<CollectedRiskSignals> {
  // Collect risk signals in parallel
  const [iovationBlackbox, hcaptchaData] = await Promise.all([
    collectSignalWithTimeout('Iovation', collectIovationBlackboxData),
    collectSignalWithTimeout('hCaptcha', collectHCaptchaData),
  ]);

  return {
    iovationBlackbox,
    hcaptchaData,
  };
}

/**
 * Collect Iovation device fingerprint using stripethirdparty
 * @returns {string} The Iovation blackbox string
 * @throws {Error} If collection fails
 */
async function collectIovationBlackboxData(): Promise<string> {
  const stripethirdpartyBaseUrl = getStripethirdpartyBaseUrl();
  const {serviceEnvironment} = getCurrentScriptUrlContext();

  devLogger.info('Start collecting Iovation blackbox data');

  let iovationIframe: ReturnType<typeof mountInvisible<'Iovation'>> | null =
    null;
  try {
    // Mount the Iovation iframe using stripethirdparty
    iovationIframe = mountInvisible<'Iovation'>({
      type: 'Iovation',
      baseURL: stripethirdpartyBaseUrl,
    });

    // Send request to collect Iovation blackbox data
    const isProd = serviceEnvironment === 'prod';
    const response = await iovationIframe.sendRequest({isProd});

    devLogger.info('Iovation blackbox data collected successfully');
    return response.blackBoxString;
  } finally {
    // Clean up the iframe
    iovationIframe?.unmount();
  }
}

/**
 * Collect hCaptcha response and key using stripethirdparty invisible captcha.
 * Uses a two-step process: initialize then execute.
 * @returns {HCaptchaData} Object with response token and key
 * @throws {Error} If collection fails
 */
async function collectHCaptchaData(): Promise<HCaptchaData> {
  const stripethirdpartyBaseUrl = getStripethirdpartyBaseUrl();
  const sitekey = getClaimInfo()?.riskSignalsHcaptchaSitekey;
  if (!sitekey) {
    devLogger.info('No hCaptcha sitekey available, skipping collection');
    throw new Error('No hCaptcha sitekey available');
  }

  devLogger.info('Start collecting hCaptcha data');

  let hcaptchaIframe: ReturnType<
    typeof mountInvisible<'HCaptchaInvisible'>
  > | null = null;

  try {
    hcaptchaIframe = mountInvisible<'HCaptchaInvisible'>({
      type: 'HCaptchaInvisible',
      baseURL: stripethirdpartyBaseUrl,
    });

    const initResponse = await hcaptchaIframe.sendRequest({
      tag: 'INITIALIZE_HCAPTCHA_INVISIBLE',
      message: {sitekey},
    });

    if (initResponse.tag !== 'LOAD_HCAPTCHA_INVISIBLE') {
      throw new Error('hCaptcha initialization failed');
    }

    const executeResponse = await hcaptchaIframe.sendRequest({
      tag: 'EXECUTE_HCAPTCHA_INVISIBLE',
      message: {sitekey, data: {}},
    });

    if (executeResponse.tag === 'RESPONSE_HCAPTCHA_INVISIBLE') {
      devLogger.info('hCaptcha data collected successfully');
      return executeResponse.value;
    } else if (executeResponse.tag === 'ERROR_HCAPTCHA_INVISIBLE') {
      throw new Error(
        `hCaptcha collection failed with error: ${executeResponse.value.error}`,
      );
    }
    throw new Error(`Unexpected hCaptcha response: ${executeResponse.tag}`);
  } finally {
    hcaptchaIframe?.unmount();
  }
}

/**
 * Send risk signals to Stripe via the /v1/risk_signals endpoint
 * @param signals The collected risk signals data
 * @returns The risk signals response from Stripe
 */
async function sendRiskSignalsToStripe(signals: CollectedRiskSignals) {
  const apiUrl = buildApiUrl('/v1/risk_signals');

  // Build request body with collected signals
  const {iovationBlackbox: iovation, hcaptchaData: hcaptcha} = signals;
  const requestBody: Record<string, any> = {};

  if (iovation) {
    requestBody.iovation_data = {io_blackbox: iovation};
  }

  if (hcaptcha) {
    const {response, key} = hcaptcha;
    requestBody.hcaptcha_data = {
      h_captcha_response: response,
      ...(key && {h_captcha_ekey: key}), // Execution key (ekey) is optional
    };
  }

  devLogger.info('Posting risk signals to /v1/risk_signals', {
    apiUrl,
    requestBody,
  });

  const fetchResponse = await fetch(apiUrl, {
    method: 'POST',
    headers: buildApiRequestHeaders(),
    body: getFormURLEncodedBody(requestBody),
  });

  return handleFetchResponse(fetchResponse);
}

/**
 * Get the stripethirdparty base URL from the claim info
 * @returns {string} The stripethirdparty base URL
 */
function getStripethirdpartyBaseUrl(): string {
  const claimInfo = getClaimInfo();
  if (!claimInfo || !claimInfo.stripethirdpartyBaseUrl) {
    throw new Error('No stripethirdpartyBaseUrl found in claimInfo');
  }
  return claimInfo.stripethirdpartyBaseUrl;
}

function buildApiRequestHeaders(): HeadersInit {
  // Get API key
  const apiKey = authStore.getApiKey('account_session');
  if (!apiKey) {
    throw new Error('No valid account session apiKey found');
  }

  // Build headers
  return getDataLayerHeaders({
    Authorization: `Bearer ${apiKey.key}`,
    'Content-Type': 'application/x-www-form-urlencoded',
  });
}

function buildApiUrl(path: string): string {
  // Get API host
  const {serviceEnvironment} = getCurrentScriptUrlContext();
  const apiHost = getStripeApiHost(serviceEnvironment);

  return `https://${apiHost}/${path}`;
}
